import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

// Finds the nearest ancestor that CAN scroll vertically. An element qualifies
// when overflow-y is auto|scroll AND it is height-constrained: either it is
// already clipping (scrollHeight > clientHeight) or it has a definite
// max-height, meaning it can clip even while content fits. Elements that
// merely grow with their content — e.g. a wrapper with overflow-x: auto whose
// computed overflow-y also reports 'auto' — are skipped: bounding to them
// feeds their own changing height back into the result, and flipping between
// "currently clipping" and "not clipping" makes the measurement oscillate
// (flickering page scrollbar).
const getVerticalScroller = el => {
    let node = el.parentElement;
    while (node && node !== document.documentElement) {
        const cs = window.getComputedStyle(node);
        const { overflowY } = cs;
        if ((overflowY === 'auto' || overflowY === 'scroll')
            && !node.hasAttribute('data-table-scroll')
            && (node.scrollHeight > node.clientHeight + 1 || cs.maxHeight !== 'none')) {
            return node;
        }
        node = node.parentElement;
    }
    return null;
};

// Distance from el's bottom edge to the true end of the scroller's content:
// following siblings at every level of the tree plus each ancestor's bottom
// padding/border/margin, up to (and including) the scroller's own padding.
// NOTE: scroller.scrollHeight cannot be used for this — when content fits, it
// equals clientHeight (the pane's full height), not the content's real bottom.
const getSpaceBelow = (el, stop) => {
    let total = 0;
    let node = el;
    while (node && node !== stop && node !== document.documentElement) {
        let sib = node.nextElementSibling;
        while (sib) {
            const cs = window.getComputedStyle(sib);
            if (cs.position !== 'fixed' && cs.position !== 'absolute') {
                total += sib.getBoundingClientRect().height
                    + (parseFloat(cs.marginTop) || 0)
                    + (parseFloat(cs.marginBottom) || 0);
            }
            sib = sib.nextElementSibling;
        }
        const parent = node.parentElement;
        if (!parent) break;
        const pcs = window.getComputedStyle(parent);
        total += (parseFloat(pcs.paddingBottom) || 0)
            + (parseFloat(pcs.borderBottomWidth) || 0);
        if (parent === stop) break;
        total += Math.max(0, parseFloat(pcs.marginBottom) || 0);
        node = parent;
    }
    return total;
};

// Scroll container for report tables: sizes itself so that the container and
// everything rendered after it (pagination, ancestor padding/margins, etc.)
// end just above the bottom of the visible area. The surrounding pane therefore
// never needs to scroll — only the table gets vertical/horizontal scrollbars,
// always visible on screen. All positions are measured in the scroller's
// content coordinate space, so the result is stable regardless of scroll
// position and independent of the container's own current height.
const TableScrollContainer = ({ children, bottomGap = 5, style, ...rest }) => {
    const ref = useRef(null);
    const lastApplied = useRef(null);

    const update = useCallback(() => {
        const el = ref.current;
        if (!el) return;

        const scroller = getVerticalScroller(el);
        const isWindow = !scroller;
        const stop = scroller || document.body;
        const scrollTop = isWindow ? window.scrollY : scroller.scrollTop;
        const scrollerTop = isWindow ? 0 : scroller.getBoundingClientRect().top;
        // Effective visible height of the scroller, capped by the viewport.
        const visibleHeight = isWindow
            ? window.innerHeight
            : Math.min(scroller.clientHeight, window.innerHeight - scrollerTop);

        // el's top within the scroller's content space — scroll-invariant.
        const layoutTop = el.getBoundingClientRect().top - scrollerTop + scrollTop;
        // Fewer than 5 rows never need a vertical scrollbar — let the table
        // display in full regardless of the space calculation.
        const rowCount = el.querySelectorAll('tbody tr').length;
        if (rowCount < 5) {
            if (lastApplied.current !== 'none') {
                el.style.maxHeight = 'none';
                lastApplied.current = 'none';
            }
            return;
        }

        // Everything below el (trailing siblings, wrapper padding/margins) must
        // stay on screen too. Independent of el's own height.
        const spaceBelow = getSpaceBelow(el, stop);

        const available = Math.max(80, visibleHeight - layoutTop - spaceBelow - bottomGap);
        // Only write when the value actually changes — avoids layout churn /
        // scrollbar flicker from repeated identical measurements.
        const target = `${available}px`;
        if (target !== lastApplied.current) {
            el.style.maxHeight = target;
            lastApplied.current = target;
        }
    }, [bottomGap]);

    // Re-measure after every render so toolbar/tab/pagination changes shrink or grow the area
    useLayoutEffect(update);

    useEffect(() => {
        window.addEventListener('resize', update);
        // Re-measure when the page's overall size changes (async images,
        // expanding toolbars, etc.) so the table area always ends above the fold.
        const observer = new ResizeObserver(update);
        observer.observe(document.body);
        return () => {
            window.removeEventListener('resize', update);
            observer.disconnect();
        };
    }, [update]);

    return (
        <div ref={ref} data-table-scroll style={{ width: '100%', overflow: 'auto', ...style }} {...rest}>
            {children}
        </div>
    );
};

export default TableScrollContainer;
